import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Reveal } from "@/components/Reveal";
import api, { formatApiError } from "@/lib/api";
import { FALLBACK_BRANCHES, GOALS, IMAGES } from "@/data/site";
import { toast } from "sonner";

const inputCls =
  "w-full bg-panel2 border border-white/10 px-4 py-3.5 text-sm text-white placeholder:text-white/30 focus:border-gold focus:outline-none transition-colors duration-300 clip-frame-sm";

export default function Contact() {
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    name: "",
    phone: "",
    email: "",
    branch: "",
    goal: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const branch = params.get("branch");
    const plan = params.get("plan");
    setForm((f) => ({
      ...f,
      branch: branch || f.branch,
      message: plan ? `Interested in the ${plan} membership.` : f.message,
    }));
  }, [params]);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.name.trim().length < 2) return toast.error("Please enter your full name.");
    if (form.phone.trim().length < 8) return toast.error("Please enter a valid phone number.");
    if (!form.branch) return toast.error("Please choose a branch.");
    if (!form.goal) return toast.error("Please choose your fitness goal.");
    setSubmitting(true);
    try {
      await api.post("/enquiries", form);
      toast.success("Enquiry received! Our team will call you back the same day.");
      setForm({ name: "", phone: "", email: "", branch: "", goal: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="pt-28 md:pt-36">
      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12">
        <Reveal>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-gold mb-4">Contact & Enquiry</p>
          <h1 className="font-display uppercase text-4xl sm:text-6xl lg:text-7xl leading-[0.95]">
            Let's talk <span className="text-gold-gradient">gains.</span>
          </h1>
          <p className="mt-6 max-w-xl text-white/60 text-sm md:text-base">
            Fill the form and the branch you choose will call you back the same day. Walk-ins welcome
            at all three houses.
          </p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-12 py-14 md:py-20 grid gap-10 lg:grid-cols-5 items-start">
        <Reveal className="lg:col-span-3">
          <form
            data-testid="enquiry-form"
            onSubmit={submit}
            className="clip-frame border border-white/10 bg-panel p-8 md:p-10 space-y-6"
          >
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="name" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                  Full Name *
                </label>
                <input
                  id="name"
                  data-testid="enquiry-form-input-name"
                  className={inputCls}
                  placeholder="Your name"
                  value={form.name}
                  onChange={set("name")}
                />
              </div>
              <div>
                <label htmlFor="phone" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                  Phone *
                </label>
                <input
                  id="phone"
                  data-testid="enquiry-form-input-phone"
                  className={inputCls}
                  placeholder="+91 98XXX XXXXX"
                  value={form.phone}
                  onChange={set("phone")}
                />
              </div>
            </div>
            <div>
              <label htmlFor="email" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                Email (optional)
              </label>
              <input
                id="email"
                type="email"
                data-testid="enquiry-form-input-email"
                className={inputCls}
                placeholder="you@example.com"
                value={form.email}
                onChange={set("email")}
              />
            </div>
            <div className="grid gap-6 sm:grid-cols-2">
              <div>
                <label htmlFor="branch" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                  Branch *
                </label>
                <select id="branch" data-testid="enquiry-form-input-branch" className={inputCls} value={form.branch} onChange={set("branch")}>
                  <option value="">Choose a branch…</option>
                  {FALLBACK_BRANCHES.map((b) => (
                    <option key={b.slug} value={b.slug}>{b.name}</option>
                  ))}
                  <option value="any">Not sure yet</option>
                </select>
              </div>
              <div>
                <label htmlFor="goal" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                  Fitness Goal *
                </label>
                <select id="goal" data-testid="enquiry-form-input-goal" className={inputCls} value={form.goal} onChange={set("goal")}>
                  <option value="">Choose a goal…</option>
                  {GOALS.map((g) => (
                    <option key={g} value={g}>{g}</option>
                  ))}
                </select>
              </div>
            </div>
            <div>
              <label htmlFor="message" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-2">
                Message
              </label>
              <textarea
                id="message"
                data-testid="enquiry-form-input-message"
                rows={4}
                className={`${inputCls} resize-none`}
                placeholder="Anything we should know? Current routine, target weight, preferred timing…"
                value={form.message}
                onChange={set("message")}
              />
            </div>
            <button
              type="submit"
              data-testid="enquiry-form-submit-button"
              disabled={submitting}
              className="w-full sm:w-auto bg-gold text-ink px-10 py-4 font-bold uppercase text-sm tracking-[0.15em] clip-frame-sm hover:bg-gold-light transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {submitting ? "Sending…" : "Send Enquiry"}
            </button>
          </form>
        </Reveal>

        <Reveal delay={0.12} className="lg:col-span-2 space-y-6">
          <div className="clip-frame border border-white/10 bg-panel p-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-gold mb-6">Our Houses</p>
            <div className="space-y-5">
              {FALLBACK_BRANCHES.map((b) => (
                <div key={b.slug} className="border-b border-white/5 pb-5 last:border-0 last:pb-0">
                  <p className="font-display uppercase text-lg">{b.name}</p>
                  <p className="mt-1 text-xs text-white/50">{b.address}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.15em] text-white/40">{b.timings}</p>
                  <div className="mt-2 flex flex-wrap gap-4 text-xs">
                    <a
                      data-testid={`contact-call-${b.slug}`}
                      href={`tel:+${b.whatsapp}`}
                      className="text-gold hover:text-gold-light transition-colors"
                    >
                      Call {b.phone}
                    </a>
                    <a
                      data-testid={`contact-whatsapp-${b.slug}`}
                      href={`https://wa.me/${b.whatsapp}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gold hover:text-gold-light transition-colors"
                    >
                      WhatsApp
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="relative overflow-hidden clip-frame border border-white/10 h-64">
            <img src={IMAGES.darkGym} alt="Oxygen Gym floor" className="absolute inset-0 h-full w-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
            <div className="absolute bottom-5 left-5">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gold">Admin?</p>
              <Link
                data-testid="contact-admin-link"
                to="/admin"
                className="text-sm text-white/70 hover:text-gold transition-colors"
              >
                View enquiries dashboard →
              </Link>
            </div>
          </div>
        </Reveal>
      </section>
    </main>
  );
}
